import GlobalTable from '@/components/probaworkflows_base_components/GlobalTable/GlobalTable.vue';
import {
  ASSIGN_WIDGET_TO_ENTITY_TYPE,
  GET_ASSIGNED_WIDGETS,
  get_headers,
  get_project_id,
  GET_WIDGETS,
  REORDER_ENTITY_TYPE_WIDGETS_URL,
  ROLES_URL,
  UNASSIGN_WIDGET_FROM_ENTITY_TYPE,
} from "@/utils/constants/constants.js";
import axios from 'axios';

import NavigationBackButton from "@/components/probaworkflows_base_components/NavigationBackButton/NavigationBackButton.vue";
import alert_mixin from "@/utils/mixins/alert_mixin";
import formatting_mixins from "@/utils/mixins/formatting_mixins";
import WidgetManagement from "@/views/probaworkflows_base/management/entity_management/entity_type_widgets/WidgetManagement.vue";
import "datatables.net";
import "datatables.net-dt/css/dataTables.dataTables.css";

export default {
  name: "ViewEntityType",
  mixins: [alert_mixin, formatting_mixins],
  components: {
    GlobalTable,
    NavigationBackButton,
    WidgetManagement
  },
  data: () => ({
    headers: null,
    project_id: get_project_id(),
    entity_type_id: sessionStorage.getItem("entity_type_id"),
    entity_type_name: sessionStorage.getItem('entity_type_name'),
    selected_widget: [],
    all_fetched_widgets: [],
    available_widgets: [],
    widget_headers: [],
    assigned_widgets: [],
    roles: [],
    selected_role: null,
    current_reorder_widgets: [],
    dragging_widget_index: null,
    is_loading: false,
  }),
  async mounted() {
    this.headers = get_headers();
    await this.get_roles();
    await this.fetch_widgets_and_assignments();
  },
  methods: {
    async fetch_widgets_and_assignments() {
      this.is_loading = true;
      await this.get_widgets();
      await this.get_assigned_widgets();
      this.sort_available_widgets_by_assignment_order();
      this.is_loading = false;
    },

    sort_available_widgets_by_assignment_order() {
      if (!this.all_fetched_widgets || this.all_fetched_widgets.length === 0) {
        this.available_widgets = [];
        return;
      }

      const assigned_widget_id_set = new Set(this.assigned_widgets);
      const widget_id_to_assigned_index_map = new Map(
        this.assigned_widgets.map((widget_id, index) => [widget_id, index])
      );

      this.available_widgets = [...this.all_fetched_widgets].sort((widget_a, widget_b) => {
        const is_widget_a_assigned = assigned_widget_id_set.has(widget_a.id);
        const is_widget_b_assigned = assigned_widget_id_set.has(widget_b.id);

        // If both assigned, sort by their assigned order
        if (is_widget_a_assigned && is_widget_b_assigned) {
          const assigned_index_a = widget_id_to_assigned_index_map.get(widget_a.id);
          const assigned_index_b = widget_id_to_assigned_index_map.get(widget_b.id);
          return assigned_index_a - assigned_index_b;
        }

        // If both unassigned, maintain original fetch order (stable by default with spread copy)
        if (!is_widget_a_assigned && !is_widget_b_assigned) {
          return 0;
        }

        // Assigned widgets come before unassigned widgets
        return is_widget_a_assigned ? -1 : 1;
      });
    },

    get_roles() {
      const query_params = { project_id: this.project_id };
      const ROLES = axios
        .get(ROLES_URL, { headers: this.headers, params: query_params })
        .then((response) => {
          const response_data = response.data.data;

          this.roles = response_data.map((role) => ({
            id: role.id,
            name: role.role,
          }));

          this.selected_role = null;
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            error.message ||
            "Failed to load roles";
          this.show_error(message);
        });
      return ROLES;
    },

    get_widgets() {
      const WIDGETS = axios
        .get(GET_WIDGETS, { headers: this.headers })
        .then((response) => {
          const response_data = response.data;
          this.all_fetched_widgets = response_data.map((widget) => ({
            id: Number(widget.id),
            name: widget.widget_display_name,
            description: widget.widget_description || "",
          }));

          this.widget_headers = Object.keys(this.all_fetched_widgets[0] || {});
          // Set available_widgets to default order initially
          this.available_widgets = [...this.all_fetched_widgets];
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            error.message ||
            "Failed to load widgets";
          this.show_error(message);
        });
      return WIDGETS;
    },

    get_assigned_widgets() {
      const params = {
        entity_type_id: this.entity_type_id
      };

      if (this.selected_role !== null) {
        params.role_id = this.selected_role;
      }

      const ASSIGNED_WIDGETS = axios
        .get(GET_ASSIGNED_WIDGETS, { params, headers: this.headers })
        .then((response) => {
          const data = response.data || [];
          this.assigned_widgets = data
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map(item => Number(item.widget.id));
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            error.message ||
            "Failed to load assigned widgets";
          this.show_error(message);
        });
      return ASSIGNED_WIDGETS;
    },

    update_widget_order() {
      if (!this.selected_role) {
        this.show_error("Please select a role first.");
        return;
      }

      if (!this.current_reorder_widgets || this.current_reorder_widgets.length === 0) {
        this.show_error("No widgets to reorder.");
        return;
      }

      const payload = {
        entity_type_id: this.entity_type_id,
        role_id: this.selected_role,
        widget_ids: this.current_reorder_widgets.map(w => w.id),
      };

      axios
        .post(REORDER_ENTITY_TYPE_WIDGETS_URL, payload, { headers: this.headers })
        .then(async () => {
          this.show_success("Widget order updated successfully.");

          await this.get_assigned_widgets();
          this.sort_available_widgets_by_assignment_order();
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            error.message ||
            "Failed to update widget order";
          this.show_error(message);
        });
    },

    async on_role_change() {
      this.is_loading = true;
      await this.get_assigned_widgets();
      this.sort_available_widgets_by_assignment_order();
      this.is_loading = false;
    },

    toggle_assignment(widget_id, is_assigned) {
      const endpoint = is_assigned
        ? ASSIGN_WIDGET_TO_ENTITY_TYPE
        : UNASSIGN_WIDGET_FROM_ENTITY_TYPE;

      const payload = {
        entity_type_id: this.entity_type_id,
        widget_id: widget_id,
      };

      if (this.selected_role !== null) {
        payload.role_id = this.selected_role;
      }

      axios
        .post(endpoint, payload, { headers: this.headers })
        .then((response) => {
          if (response.data.is_assigned) {
            if (!this.assigned_widgets.includes(widget_id)) {
              this.assigned_widgets.push(widget_id);
            }
          } else {
            this.assigned_widgets = this.assigned_widgets.filter(
              (id) => id !== widget_id
            );
          }
          this.sort_available_widgets_by_assignment_order();
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            error.message ||
            "Failed to toggle widget assignment";
          this.show_error(message);
        });
    },

    open_reorder_widgets_modal() {
      const widget_map = new Map(
        (this.all_fetched_widgets || []).map(w => [Number(w.id), w])
      );

      // Create list in the exact order of assigned_widgets
      this.current_reorder_widgets = (this.assigned_widgets || [])
        .map(id => widget_map.get(Number(id)))
        .filter(Boolean); // remove missing ones safely
    },



    drag_start_widget(event, index) {
      this.dragging_widget_index = index;
      event.dataTransfer.effectAllowed = "move";
    },

    drag_drop_widget(event, drop_index) {
      if (this.dragging_widget_index === null) return;

      const from_index = this.dragging_widget_index;
      const to_index = drop_index;

      if (from_index === to_index) return;

      const updated = [...this.current_reorder_widgets];
      const [moved] = updated.splice(from_index, 1);
      updated.splice(to_index, 0, moved);

      this.current_reorder_widgets = updated;
      this.dragging_widget_index = null;
    },

    drag_end_widget() {
      this.dragging_widget_index = null;
    },

  }
};